import { FC } from 'react';

import { Button } from '@new/common/ui/Button/Button';
import { Select } from '@new/common/ui/Select/Select';

import { useDegreeChoice } from '../../hooks/useDegreeChoice';
import { PlanStepCard } from '../PlanStepCard/PlanStepCard';

const TITLE = 'Choose your degree';
const DESCRIPTION =
  "Pick the degree you want to complete. We'll build your course plan for it — when you save the plan, you're enrolled in it.";
const TYPE_LABEL = 'Degree type';
const PROGRAM_LABEL = 'Program';

interface ChooseDegreeStepProps {
  initialDegreeId: number | null;
  onGenerate: (degreeId: number) => void;
}

export const ChooseDegreeStep: FC<ChooseDegreeStepProps> = ({ initialDegreeId, onGenerate }) => {
  const choice = useDegreeChoice(initialDegreeId);

  const onSeePlan = () => {
    if (choice.selectedProgramId != null) {
      onGenerate(choice.selectedProgramId);
    }
  };

  return (
    <PlanStepCard>
      <div className="flex flex-col gap-3">
        <h2 className="m-0! [font-family:var(--font-arvo)]! [font-size:var(--fs-h4)]! font-bold text-(--neutral-black-NB900)!">
          {TITLE}
        </h2>
        <p className="m-0! [font-size:var(--fs-p3)]! text-(--neutral-black-NB700)! leading-tight">
          {DESCRIPTION}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="l4 medium text-(--neutral-black-NB900)!">{TYPE_LABEL}</span>
          <Select
            value={choice.selectedType}
            onChange={choice.onTypeChange}
            options={choice.typeOptions}
            placeholder={choice.isLoading ? 'Loading degrees…' : 'Select a degree type…'}
            disabled={choice.isLoading}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="l4 medium text-(--neutral-black-NB900)!">{PROGRAM_LABEL}</span>
          <Select
            value={choice.selectedProgramId != null ? String(choice.selectedProgramId) : ''}
            onChange={choice.onProgramChange}
            options={choice.programOptions}
            placeholder={choice.hint ? 'Not available' : 'Select a program…'}
            disabled={choice.programOptions.length === 0}
          />
          <p className="m-0! min-h-10 [font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
            {choice.hint}
          </p>
        </div>
      </div>

      <Button
        variant="primary"
        className="w-full"
        onClick={onSeePlan}
        disabled={choice.selectedProgramId == null}
        text="See my plan"
      />
    </PlanStepCard>
  );
};
